/**
 * ErrorBoundary Component
 * 
 * 全局錯誤邊界組件
 * 捕獲並處理應用程式中的錯誤
 */

import React from 'react';
import { Box, Typography, Button, Stack, Alert } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BugReportIcon from '@mui/icons-material/BugReport';
import logger from '../../utils/logger';
import { handleError } from '../../utils/errorHandler';
import { openSentryFeedback } from '../../utils/sentryFeedback';
import { isChunkLoadError } from '../../utils/lazyWithRetry';

class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // 更新 state 使下一次渲染能夠顯示降級後的 UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // 記錄錯誤到錯誤報告服務
    logger.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // 處理錯誤
    const errorData = handleError(error, 'ErrorBoundary', 'tc');
    
    this.setState({
      error,
      errorInfo,
      errorData,
    });

    // 發送錯誤到 Sentry（如果已配置）
    if (process.env.REACT_APP_SENTRY_DSN && process.env.NODE_ENV === 'production') {
      import('@sentry/react').then((Sentry) => {
        Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack,
            },
          },
          tags: {
            errorBoundary: true,
          },
        });
      }).catch((err) => {
        console.warn('Failed to send error to Sentry:', err);
      });
    }
  }

  handleReset = () => {
    if (isChunkLoadError(this.state.error) && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('v', String(Date.now()));
      window.location.replace(url.toString());
      return;
    }
    this.setState({ hasError: false, error: null, errorInfo: null });
    // 可以選擇重新載入頁面
    // window.location.reload();
  };

  // 錯誤恢復機制：嘗試重新渲染
  componentDidUpdate(prevProps) {
    // 如果 props 改變且之前有錯誤，嘗試恢復
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  render() {
    if (this.state.hasError) {
      // 可以使用自定義的降級 UI
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

// 錯誤降級 UI 組件
function ErrorFallback({ error, errorInfo, onReset }) {
  const [copySucceeded, setCopySucceeded] = React.useState(false);

  // 注意：這裡不能依賴 useLanguage hook，因為 Provider 也可能發生錯誤
  const language = (() => {
    try {
      const storedLanguage = window?.localStorage?.getItem('savedLanguage');
      return storedLanguage === 'en' ? 'en' : 'tc';
    } catch (err) {
      return 'tc';
    }
  })();

  const errorSummary = React.useMemo(() => {
    const lines = [
      `Time: ${new Date().toISOString()}`,
      `URL: ${window?.location?.href || ''}`,
      `UserAgent: ${navigator?.userAgent || ''}`,
      `Error: ${error?.toString?.() || 'Unknown error'}`,
      errorInfo?.componentStack ? `ComponentStack:${errorInfo.componentStack}` : '',
    ].filter(Boolean);

    return lines.join('\n');
  }, [error, errorInfo]);

  const handleCopyError = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(errorSummary);
      setCopySucceeded(true);
      window.setTimeout(() => setCopySucceeded(false), 2000);
    } catch (err) {
      setCopySucceeded(false);
      console.warn('Failed to copy error summary:', err);
    }
  }, [errorSummary]);

  const handleReportError = React.useCallback(async () => {
    const openGithubIssue = () => {
      const title = encodeURIComponent(language === 'tc' ? '應用程式崩潰回報' : 'App crash report');
      const body = encodeURIComponent(errorSummary);
      window.open(`https://github.com/leowongco/hk-railway-display-2.0/issues/new?title=${title}&body=${body}`, '_blank', 'noopener,noreferrer');
    };

    try {
      if (!process.env.REACT_APP_SENTRY_DSN) {
        openGithubIssue();
        return;
      }

      await openSentryFeedback({
        language,
        title: language === 'tc' ? '應用程式錯誤回報' : 'App Error Report',
        labelComments: language === 'tc' ? '錯誤情境與步驟' : 'Error context and steps',
      });
    } catch (err) {
      openGithubIssue();
    }
  }, [errorSummary, language]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        backgroundColor: (theme) => theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          maxWidth: 600,
          width: '100%',
          p: 4,
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Stack spacing={3} alignItems="center">
          <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main' }} />
          
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            {language === 'tc' ? '發生錯誤' : 'Something Went Wrong'}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" align="center">
            {language === 'tc'
              ? '應用程式遇到了一個錯誤。請嘗試重新整理頁面或返回首頁。'
              : 'The application encountered an error. Please try refreshing the page or returning to the homepage.'}
          </Typography>

          {process.env.NODE_ENV === 'development' && error && (
            <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {language === 'tc' ? '錯誤詳情（開發模式）' : 'Error Details (Development Mode)'}
              </Typography>
              <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
                {error.toString()}
                {errorInfo && errorInfo.componentStack}
              </Typography>
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={onReset}
            >
              {language === 'tc' ? '重試' : 'Retry'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => (window.location.href = '/')}
            >
              {language === 'tc' ? '返回首頁' : 'Go Home'}
            </Button>
          </Stack>

          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              color={copySucceeded ? 'success' : 'primary'}
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyError}
            >
              {copySucceeded
                ? (language === 'tc' ? '已複製' : 'Copied')
                : (language === 'tc' ? '複製錯誤資訊' : 'Copy Error Info')}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<BugReportIcon />}
              onClick={handleReportError}
            >
              {language === 'tc' ? '回報錯誤' : 'Report Error'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

// 使用 HOC 包裝以支持語言 Context
function ErrorBoundaryWithLanguage({ children }) {
  return <ErrorBoundaryClass>{children}</ErrorBoundaryClass>;
}

export default ErrorBoundaryWithLanguage;
